/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PopUpModal from '../../PopUpModal/PopUpModal';
import { XIcon, LockClosedIcon } from '@heroicons/react/solid';
import WorkflowProgressIndicator from '../WorkflowProgressIndicator';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ applicationId, onSuccess, onError, registrationFee, eventName }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        const functions = getFunctions();
        const createPayment = httpsCallable(functions, 'createVendorRegistrationPayment');
        
        const result = await createPayment({ applicationId });
        
        setClientSecret(result.data.clientSecret);
        setPaymentIntent(result.data);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        setError(err.message || 'Failed to initialize payment');
        onError(err.message);
      }
    };

    if (applicationId) {
      initializePayment();
    }
  }, [applicationId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret || isProcessing) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
        onError(result.error.message);
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          onSuccess();
        }
      }
    } catch (err) {
      setError(err.message);
      onError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1f2937',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Complete Your Registration
        </h3>
        <p className="text-sm text-gray-600">
          One final step to participate in <span className="font-medium">{eventName}</span>
        </p>
      </div>

      {paymentIntent && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-5 space-y-4">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-1">Total Amount Due</p>
              <p className="text-2xl font-bold text-primary-900">
                ${(paymentIntent.amount / 100).toFixed(2)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-primary-700">Platform Fee</p>
              <p className="text-lg font-semibold text-primary-900">
                ${(paymentIntent.platformFee / 100).toFixed(2)} (15%)
              </p>
            </div>
          </div>
          <div className="pt-3 border-t border-primary-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium text-primary-800">Your Earnings After Fee</p>
              <p className="text-lg font-bold text-green-600">
                ${((paymentIntent.amount - paymentIntent.platformFee) / 100).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="border border-gray-300 rounded-lg p-4 bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <LockClosedIcon className="h-4 w-4 text-gray-600" />
          Card Information
        </label>
        <CardElement options={cardElementOptions} />
        <p className="text-xs text-gray-500 mt-3 text-center">
          Secure payment processed by Stripe. Your card details are never stored.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing || !clientSecret}
        className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
          !stripe || isProcessing || !clientSecret
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-pebble-red hover:bg-red-700 active:scale-95'
        }`}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing Payment...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <LockClosedIcon className="h-4 w-4" />
            Pay ${paymentIntent ? (paymentIntent.amount / 100).toFixed(2) : '0.00'}
          </span>
        )}
      </button>

      <p className="text-xs text-gray-600 text-center leading-relaxed">
        By clicking "Pay", you confirm this registration for the event. Your receipt will be sent to your email.
      </p>
    </form>
  );
};

const VendorPaymentModal = ({ isOpen, onClose, applicationId, eventName, registrationFee }) => {
  const [paymentStatus, setPaymentStatus] = useState('form'); // 'form', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSuccess = () => {
    setPaymentStatus('success');
    setTimeout(() => {
      onClose(true);
    }, 2000);
  };

  const handleError = (message) => {
    setPaymentStatus('error');
    setErrorMessage(message);
  };

  const handleClose = () => {
    if (paymentStatus !== 'success') {
      onClose(false);
    }
  };

  return (
    <PopUpModal open={isOpen} handleClose={handleClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 bg-white rounded-full p-1"
        >
          <XIcon className="h-6 w-6" />
        </button>

        {paymentStatus === 'form' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Left: Workflow Progress */}
            <div className="bg-grey-50 p-6 md:p-8 border-r border-grey-200">
              <WorkflowProgressIndicator
                currentStatus="Approved"
                registrationFee={registrationFee}
                eventName={eventName}
              />
            </div>

            {/* Right: Payment Form */}
            <div className="p-6 md:p-8">
              <Elements stripe={stripePromise}>
                <PaymentForm
                  applicationId={applicationId}
                  eventName={eventName}
                  registrationFee={registrationFee}
                  onSuccess={handleSuccess}
                  onError={handleError}
                />
              </Elements>
            </div>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
              <svg
                className="h-10 w-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-3">
              Registration Complete! 🎉
            </h3>
            <p className="text-gray-600 mb-6">
              Your vendor registration has been confirmed. A receipt and event details have been sent to your email.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                You're all set to participate in the event. Check your email for booth assignments and event logistics.
              </p>
            </div>
          </div>
        )}

        {paymentStatus === 'error' && (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
              <XIcon className="h-10 w-10 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Payment Failed
            </h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{errorMessage}</p>
            </div>
            <p className="text-gray-600 mb-6">
              Please check your card details and try again, or contact support if the problem persists.
            </p>
            <button
              onClick={() => setPaymentStatus('form')}
              className="px-6 py-3 bg-pebble-red text-white rounded-lg font-semibold hover:bg-red-700 transition-all"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </PopUpModal>
  );
};

export default VendorPaymentModal;
